import { storageKey } from "@/site";
import { SIGN_IN_PAGE } from "./links";

// Device-local only: a convenience for whoever uses this device, never
// synced and never tied to a reader or an account.
export const ALWAYS_GOOGLE_KEY = storageKey("alwaysGoogle.v1");

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const browserStorage = (): Store | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

export function alwaysUsesGoogle(
  storage: Store | null = browserStorage(),
): boolean {
  return storage?.getItem(ALWAYS_GOOGLE_KEY) === "1";
}

export function rememberAlwaysGoogle(
  storage: Store | null = browserStorage(),
): void {
  storage?.setItem(ALWAYS_GOOGLE_KEY, "1");
}

export function forgetAlwaysGoogle(
  storage: Store | null = browserStorage(),
): void {
  storage?.removeItem(ALWAYS_GOOGLE_KEY);
}

// Per tab: a Google sign-in this tab started recently that brought the
// person back to /sign-in did not finish (cancelled, failed, or Back), so
// the page must not send them straight round again.
export const GOOGLE_ATTEMPT_KEY = storageKey("googleAttempt.v1");
export const GOOGLE_ATTEMPT_WINDOW_MS = 10 * 60 * 1000;

const tabStorage = (): Store | null => {
  try {
    return sessionStorage;
  } catch {
    return null;
  }
};

export function markGoogleAttempt(
  now: number = Date.now(),
  storage: Store | null = tabStorage(),
): void {
  storage?.setItem(GOOGLE_ATTEMPT_KEY, String(now));
}

export function recentGoogleAttempt(
  now: number = Date.now(),
  storage: Store | null = tabStorage(),
): boolean {
  const at = Number(storage?.getItem(GOOGLE_ATTEMPT_KEY) ?? Number.NaN);
  return Number.isFinite(at) && now - at < GOOGLE_ATTEMPT_WINDOW_MS;
}

const SIGN_IN_ERROR_PARAMS = ["error", "auth_error"] as const;

/**
 * A finished sign-in lands on its returnTo with no error; an unfinished one
 * comes back to /sign-in, or to returnTo with `auth_error`. So a page load
 * anywhere else, without an error, ends this tab's attempt, and the next
 * /sign-in may continue with Google on its own again.
 */
export function settleGoogleAttempt(
  { pathname, search }: Pick<Location, "pathname" | "search">,
  storage: Store | null = tabStorage(),
): void {
  const onSignIn =
    pathname === SIGN_IN_PAGE || pathname.startsWith(`${SIGN_IN_PAGE}/`);
  const params = new URLSearchParams(search);
  if (onSignIn || SIGN_IN_ERROR_PARAMS.some((name) => params.has(name))) return;
  storage?.removeItem(GOOGLE_ATTEMPT_KEY);
}
